import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { ImmersiveGameBase } from '../_shared/immersive-game.base';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { MatchRound, MatchUpState } from '../../../interfaces/match-up.interface';

/**
 * Host shell for Star Match — the drag-numbers-and-letters skill game.
 *
 * Same composition as the other Game-World skill games: state
 * (`MatchUpState`) lives in `MiniGameService.session$`; this shell only
 * reads it and writes outcomes via `scoreMatchUpRound` +
 * `advanceMatchUpRound`. The interactive board is
 * `<app-match-up-stage>`, which emits `placed` (per correct tile) and
 * `solved` (round complete).
 */
@Component({
  selector: 'app-match-up-game',
  templateUrl: './match-up-game.component.html',
  styleUrls: ['./match-up-game.component.scss']
})
export class MatchUpGameComponent extends ImmersiveGameBase implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;

  showSuccess = false;
  resolving = false;
  lastXp = 0;

  private prevRound = -1;
  private pendingTimeouts: any[] = [];

  constructor(
    private miniGameService: MiniGameService,
    private gameAudioService: GameAudioService,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.session = s;
        const round = this.state?.currentRound ?? -1;
        if (round !== this.prevRound) {
          this.prevRound = round;
          this.showSuccess = false;
          this.resolving = false;
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => { if (r) this.lastXp = r.xpEarned; });
  }

  ngOnDestroy(): void {
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.destroy$.next();
    this.destroy$.complete();
  }

  private later(fn: () => void, ms: number): void {
    this.pendingTimeouts.push(setTimeout(fn, ms));
  }

  // ── Derived state ──────────────────────────────────────────────────

  get state(): MatchUpState | null {
    return (this.session?.gameState as MatchUpState) ?? null;
  }

  get round(): MatchRound | null {
    const s = this.state;
    if (!s) return null;
    return s.rounds[s.currentRound] ?? null;
  }

  get currentRound(): number { return this.state?.currentRound ?? 0; }
  get totalRounds(): number { return this.state?.totalRounds ?? 0; }
  get roundDots(): number[] { return Array.from({ length: this.totalRounds }, (_, i) => i); }
  get solvedFlags(): boolean[] { return this.state?.solved ?? []; }

  get prompt(): string {
    const r = this.round;
    if (!r) return '';
    return this.currLang === 'ar' ? r.prompt.ar : r.prompt.en;
  }

  get student(): MiniGameStudent | null {
    const s = this.state;
    if (!s || s.currentStudentId == null) return null;
    return this.session?.students.find(st => st.id === s.currentStudentId) ?? null;
  }

  get studentName(): string {
    const st = this.student;
    if (!st) return '';
    return (this.currLang === 'ar' ? st.name.ar : st.name.en || '').split(' ')[0];
  }

  // ── Stage events ───────────────────────────────────────────────────

  onPlaced(): void {
    // Per-tile placement is celebrated visually (sparkle in the stage)
    // only — no per-tile sound, so 4 correct drops don't stack into a
    // noisy round. The applause is saved for the whole-round solve.
  }

  onSolved(): void {
    if (this.resolving) return;
    this.resolving = true;
    const a = this.gameAudioService.play('assets/game-mode/sound-effects/applauseSoundEffect.mp3');
    if (a) this.later(() => this.gameAudioService.stop(a), 2500);
    this.miniGameService.scoreMatchUpRound(true);
    this.showSuccess = true;
    this.later(() => this.miniGameService.advanceMatchUpRound(), 1800);
  }

  skip(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.miniGameService.scoreMatchUpRound(false);
    this.later(() => this.miniGameService.advanceMatchUpRound(), 250);
  }
}
